import i18n from "@/i18n";
import Axios from "axios";
import _ from "@/boot/lodash";
import { ILanguage } from "@/models/constants";
import { ActionTree, GetterTree, MutationTree } from "vuex";
import { localize } from "vee-validate";
import { ToastProgrammatic as $toast } from "buefy";
import { IState } from "@/store";
import {
  SupportedLocaleCodes,
  getSupportedLocaleCode
} from "@/data/enums/i18n";

export interface Ii18nState {
  locales: ILocale[];
  activeLocaleCode: SupportedLocaleCodes;
}

export interface ILocale {
  language: ILanguage;
  packs: any[];
}

const initialState = (): Ii18nState => ({
  activeLocaleCode: SupportedLocaleCodes.EN,
  locales: [
    {
      language: {
        code: SupportedLocaleCodes.EN
      } as ILanguage,
      packs: []
    }
  ]
});

const getters: GetterTree<Ii18nState, IState> = {
  // Get supported brand locales
  brandLocales: (s, g, rootState): SupportedLocaleCodes[] => {
    return _.map(rootState?.brand?.languages || [], ({ code }) => code);
  },
  // Get default brand locale
  brandLocale: (s, g, rootState) => {
    const { languages, language_id } = rootState?.brand;
    return _.find(languages, ({ id }) => id === language_id)?.code;
  },
  /**
   * @name $store.getters["i18n/key"]
   * @param key String
   * @desc Takes a key and appends the current active ISO-639 locale code.
   * @returns New 'locale-aware' key that can be applied to other components
   * (eg <u-link>) to prevent component re-use.
   */
  key: ({ activeLocaleCode }) => (key: string) => {
    return `${key}_${activeLocaleCode}`;
  }
};

const actions: ActionTree<Ii18nState, IState> = {
  setDefaultLocale: ({ commit, getters, rootGetters }) => {
    // Get local locale (from localStorage or browser)
    const localLocale = (
      (window.location.pathname.startsWith("/admin")
        ? localStorage.getItem("i18n/active_admin_locale_code")
        : undefined) ??
      localStorage.getItem("i18n/active_locale_code") ??
      window.navigator.language
    )?.replace("-", "_") as SupportedLocaleCodes;

    // Set localeCode (checking brand support)
    const localeCode = getSupportedLocaleCode(
      getters["brandLocales"].includes(localLocale)
        ? localLocale
        : getters["brandLocale"]
    );

    // Switch i18n locale
    i18n.locale = localeCode;

    // Commit active locale
    commit("setLocaleCode", {
      localeCode,
      isAdminContext: rootGetters.isAdminContext
    });
  },
  setLanguage: (
    { state, getters, rootGetters, commit, dispatch },
    localeCode: SupportedLocaleCodes
  ) => {
    return new Promise(resolve => {
      if (!getters["brandLocales"].includes(localeCode))
        return resolve(state.activeLocaleCode);
      if (localeCode === state.activeLocaleCode) {
        dispatch("loadValidationLocale", { localeCode });
        resolve(localeCode);
        return;
      } else {
        commit("setLocaleCode", {
          localeCode,
          isAdminContext: rootGetters.isAdminContext
        });
        resolve(localeCode);
        return window.location.reload();
      }
    });
  },

  loadLanguagePacks: (
    { dispatch, commit },
    { localeCode = i18n.locale, packs }: { localeCode: string; packs: [] }
  ) => {
    return new Promise<void>(resolve => {
      Promise.all(
        _.map(packs, pack => {
          return dispatch("loadLanguagePack", { localeCode, pack });
        })
      ).then(packs => {
        const messages = {};
        _.each(packs, pack => {
          if (!pack) return;

          /**
           * Translates files with dot in the name into nested objects
           * .ie _modules.web_hosting => {_modules: {web_hosting: {...}}}
           */
          const paths = pack.name.split(".");
          let curr = messages;
          while (paths.length) {
            const path = paths.shift();
            if (!paths.length) curr[path] = pack.messages;
            else {
              curr[path] = {};
              curr = curr[path];
            }
          }

          commit("addLocalePack", {
            localeCode,
            pack: pack.name
          });
        });
        if (!_.isEmpty(messages)) {
          i18n.mergeLocaleMessage(localeCode, messages);
          i18n.setLocaleMessage(localeCode, i18n.messages[localeCode]);
        }
        resolve();
      });
    });
  },

  fetchLanguageFile: (
    ctx,
    {
      localeCode = i18n.locale,
      name,
      path
    }: { localeCode: string; name: string; path?: string }
  ) => {
    const version = process.env.VERSION;

    return new Promise(resolve => {
      Axios.get(path || `/languages/${localeCode}/${name}.json?${version}`)
        .then(response => {
          resolve({
            name,
            messages: response.data
          });
        })
        .catch(() => {
          $toast.open({
            message: i18n.t("_sentence.language_pack_missing", {
              pack: `${localeCode}/${name}`
            }) as string,
            type: "is-danger"
          });
          resolve(null); // Pack not found
        });
    });
  },

  loadLanguagePack: (
    { dispatch, state, commit },
    {
      localeCode = i18n.locale,
      path,
      pack
    }: { localeCode: string; path: null; pack: [] }
  ) => {
    return new Promise(resolve => {
      let locale = _.find(
        state.locales,
        (l: ILocale) => l.language.code === localeCode
      ) as ILocale | undefined;

      // Check for invalid locale
      if (locale === undefined) {
        locale = {
          language: {
            code: localeCode
          } as ILanguage,
          packs: []
        } as ILocale;
        commit("addLocale", { locale });
      }

      // Pack already loaded
      if (locale.packs.includes(pack)) {
        return resolve(null);
      }

      // Get language pack
      dispatch("fetchLanguageFile", {
        localeCode,
        name: pack,
        path
      }).then(resolve);
    });
  },

  loadValidationLocale: (
    // eslint-disable-next-line no-empty-pattern
    {},
    { localeCode = i18n.locale }: { localeCode: string }
  ) => {
    return import(`vee-validate/dist/locale/${localeCode}.json`).then(
      locale => {
        localize(localeCode, locale);
      }
    );
  }
};

const mutations: MutationTree<Ii18nState> = {
  addLocale: (state: Ii18nState, payload: { locale: ILocale }) => {
    const index = _.findIndex(
      state.locales,
      (l: ILocale) => l.language.code === payload.locale.language.code
    );

    if (index !== -1) {
      _.merge(state.locales[index], payload.locale);
      return;
    }
    state.locales.push(payload.locale);
  },
  setLocaleCode: (
    state: Ii18nState,
    {
      localeCode,
      isAdminContext
    }: { localeCode: SupportedLocaleCodes; isAdminContext: boolean }
  ) => {
    state.activeLocaleCode = localeCode;
    if (isAdminContext)
      return localStorage.setItem("i18n/active_admin_locale_code", localeCode);
    localStorage.setItem("i18n/active_locale_code", localeCode);
  },
  addLocalePack: (
    state: Ii18nState,
    { localeCode, pack }: { localeCode: string; pack: string }
  ) => {
    const index = _.findIndex(
      state.locales,
      (l: ILocale) => l.language.code === localeCode
    );

    // Check for invalid locale
    if (index === -1) return false;
    state.locales[index].packs.push(pack);
  }
};

export default {
  namespaced: true,
  state: initialState(),
  getters,
  actions,
  mutations
};
